import Vue from 'vue';
import { map, filter, get, assignIn, isEqual, keyBy, values } from 'lodash';
import diff from 'deep-diff';
import { getUsers } from 'store/queries/audience';
import {
  usersCf,
  applyFilters,
  resetFilters,
  getDimensions,
} from 'store/filters/audience';
import router from 'router';
import { configs } from 'config';

const state = {
  userMap: {},
  users: [],
  filters: [],
  appliedFilters: [],
  activeTrackerCategory: 'present',
  activeBoardView: 'summary',
  completed: false,
};

const getters = {};

const actions = {
  async loadUsers({ commit, dispatch, state, rootState }, after) {
    if (state.users.length >= configs().USERS_LIMIT) {
      dispatch('completed');

      console.log(
        'Reached users limit, stopping loading of users to keep performance'
      );

      return;
    }

    const res = await getUsers(rootState.application.appId, after);
    const data = get(res, 'body.data.app.active_users');
    const appId = get(res, 'body.data.app.id');

    if (appId !== rootState.application.appId) {
      console.log(
        `Queried active users from app ${appId} can not be merged with selected app ${
          rootState.application.appId
        }`
      );
      return;
    }

    if (!data) {
      console.log('No active users found');
      return;
    }

    const users = data.slice;
    after = data.paging.cursors.after;

    if (users && users.length === 0) {
      dispatch('completed');

      return;
    }

    commit('users', { users });
    dispatch('loadUsers', after);
  },
  completed({ commit, dispatch, rootState }) {
    commit('completed');

    if (
      Vue.localStorage.get('no-hose') ||
      !configs().HAS_FIREHOSE ||
      rootState.application.appId !== configs().JOURNEYS_APP_ID
    ) {
      return;
    }

    dispatch('firehose/scheduleUpdates', null, { root: true });
  },
};

const mutations = {
  users(state, payload) {
    if (payload.users.length === 0) {
      return;
    }

    const users = map(payload.users, user => {
      if (!user.prediction_tree && !user.prediction_tree.events.length) {
        return assignIn({}, user, getDimensions(user));
      }

      const userWithPredictions = {
        ...user,
        prediction_tree: {
          ...user.prediction_tree,
          events: user.prediction_tree.events.map(e => {
            if (e.type === 'StationaryPrediction') {
              return { ...e, location_type: e.location_type.toLowerCase() };
            }
            return { ...e, mode: e.mode.toLowerCase() };
          }),
        },
      };

      return assignIn(
        {},
        userWithPredictions,
        getDimensions(userWithPredictions)
      );
    });

    const oldState = assignIn({}, state.userMap);
    const newState = assignIn({}, state.userMap, keyBy(users, 'id'));

    if (!diff.diff(oldState, newState)) {
      return;
    }

    state.userMap = assignIn({}, state.userMap, keyBy(users, 'id'));

    resetFilters();
    usersCf.remove();
    usersCf.add(values(state.userMap));
    applyFilters(state.appliedFilters);

    state.users = usersCf.allFiltered();
  },
  clearUsers(state) {
    usersCf.remove();
    state.userMap = {};
    state.users = [];
  },
  setActiveBoardView(state, view) {
    if (/^(summary|detail)$/.test(view)) {
      state.activeBoardView = view;

      if (get(router, 'currentRoute.query.view') !== view) {
        router.replace({ query: { view } });
      }
    }
  },
  setActiveCategory(state, category) {
    state.activeTrackerCategory = category;
  },
  addFilterItem(state, item) {
    state.filters = [...state.filters, item];
  },
  removeFilterItem(state, item) {
    state.filters = filter(state.filters, filter => !isEqual(filter, item));
  },
  clearFilters(state) {
    state.filters = [];
  },
  applyFilters(state) {
    state.appliedFilters = state.filters;

    resetFilters();
    applyFilters(state.appliedFilters);

    state.users = usersCf.allFiltered();
  },
  resetFilters(state) {
    state.filters = state.appliedFilters;
  },
  completed(state) {
    state.completed = true;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
